import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePasswordReset } from '../../hooks/usePasswordReset';

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const {
    email,
    setEmail,
    isRequesting,
    requestSuccess,
    errors,
    requestPasswordReset,
  } = usePasswordReset();

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="sticky top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <Link to="/" className="flex items-center">
              <span className="text-xl sm:text-2xl font-bold">
                <span className="text-yellow-400">Nutri</span>
                <span className="text-white">Bot</span>
              </span>
            </Link>
            <Link
              to="/login"
              className="flex items-center gap-2 text-xs sm:text-sm text-gray-300 hover:text-yellow-400 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              <span>{t('auth.forgotPassword.backToLogin')}</span>
            </Link>
          </div>
        </div>
      </header>

      <div className="min-h-[calc(100vh-56px)] sm:min-h-[calc(100vh-64px)] flex items-center justify-center px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-md w-full">

          {!requestSuccess ? (
            <>
              {/* Icon */}
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-yellow-500/20 rounded-2xl flex items-center justify-center">
                  <i className="ri-lock-password-line text-yellow-400 text-3xl"></i>
                </div>
              </div>

              {/* Headline */}
              <div className="text-center mb-6 sm:mb-8">
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">
                  {t('auth.forgotPassword.title')}
                </h1>
                <p className="text-lg sm:text-xl text-gray-300">
                  {t('auth.forgotPassword.subtitle')}
                </p>
              </div>

              <div className="bg-gray-900 rounded-2xl p-6 sm:p-8 border border-gray-800">
                <form onSubmit={requestPasswordReset} className="space-y-5 sm:space-y-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                      {t('auth.forgotPassword.emailLabel')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`w-full px-4 py-3 bg-gray-800 text-white rounded-lg border ${
                        errors.email ? 'border-red-500' : 'border-gray-700'
                      } focus:border-yellow-400 focus:outline-none transition`}
                      placeholder={t('auth.forgotPassword.emailPlaceholder')}
                      disabled={isRequesting}
                      autoFocus
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-400">{errors.email}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isRequesting || !email.trim()}
                    className="w-full py-3 sm:py-4 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold rounded-xl hover:from-yellow-500 hover:to-yellow-700 transition-ball duration-200 cursor-pointer shadow-lg hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    {isRequesting ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin text-lg"></i>
                        {t('auth.forgotPassword.sending')}
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-mail-send-line text-lg"></i>
                        {t('auth.forgotPassword.submitButton')}
                      </span>
                    )}
                  </button>
                </form>

                {/* Security Note */}
                <div className="mt-6 flex items-start gap-3 bg-gray-800/50 rounded-lg px-4 py-3">
                  <i className="ri-shield-check-line text-yellow-400 text-lg flex-shrink-0 mt-0.5"></i>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    {t('auth.forgotPassword.securityNote')}
                  </p>
                </div>
              </div>

              <div className="mt-6 text-center">
                <Link
                  to="/login"
                  className="text-sm text-gray-400 hover:text-yellow-400 transition-colors"
                >
                  <i className="ri-arrow-left-line mr-1"></i>
                  {t('auth.forgotPassword.backToLogin')}
                </Link>
              </div>
            </>
          ) : (
            <>
              {/* Success Icon */}
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-green-500/20 rounded-2xl flex items-center justify-center">
                  <i className="ri-mail-check-line text-green-400 text-3xl"></i>
                </div>
              </div>

              {/* Success Headline */}
              <div className="text-center mb-6 sm:mb-8">
                <h1 className="text-3xl sm:text-4xl font-bold mb-3 text-green-400">
                  {t('auth.forgotPassword.checkEmail')}
                </h1>
                <p className="text-gray-300 text-sm sm:text-base">
                  {t('auth.forgotPassword.successMessage')}
                </p>
              </div>

              <div className="bg-gray-900 rounded-2xl p-6 sm:p-8 border border-gray-800">
                {/* Email shown */}
                <div className="flex items-center gap-3 bg-gray-800 rounded-lg px-4 py-3 mb-6">
                  <i className="ri-mail-line text-yellow-400 text-lg flex-shrink-0"></i>
                  <p className="text-sm text-white font-medium truncate">{email}</p>
                </div>

                {/* Help list */}
                <div className="mb-6">
                  <p className="text-sm text-gray-400 mb-3">
                    {t('auth.forgotPassword.didntReceive')}
                  </p>
                  <ul className="space-y-2">
                    {[
                      t('auth.forgotPassword.hint1'),
                      t('auth.forgotPassword.hint2'),
                      t('auth.forgotPassword.hint3'),
                    ].map((hint, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-gray-500">
                        <i className="ri-arrow-right-s-line text-gray-600 mt-0.5 flex-shrink-0"></i>
                        {hint}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => {
                      // Reset success state to try different email
                      window.location.href = '/forgot-password';
                    }}
                    className="w-full py-3 bg-gray-800 text-white rounded-xl border border-gray-700 hover:bg-gray-700 transition cursor-pointer font-medium text-sm"
                  >
                    <i className="ri-mail-line mr-2"></i>
                    {t('auth.forgotPassword.tryDifferentEmail')}
                  </button>

                  <Link
                    to="/login"
                    className="flex items-center justify-center gap-2 w-full py-3 text-yellow-400 hover:text-yellow-300 font-semibold text-sm transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    {t('auth.forgotPassword.backToLogin')}
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}